/**
 * Pulls the audio out of a dropped video as an MP3, in the browser, so only the
 * soundtrack crosses the network - a lecture video is gigabytes, its audio a
 * few tens of MB. Mediabunny streams the file through `BlobSource`, so the
 * video itself is never held in memory, and the whole library (plus the LAME
 * encoder) is a lazy chunk that loads only when a video is actually dropped.
 */

/** Browser-side cap on a video: only its audio is uploaded, so this is generous. */
export const MAX_VIDEO_BYTES = 4 * 1024 * 1024 * 1024;

/** 64 kbps mono: clear speech, ~29 MB for 90 minutes. The server compacts it again. */
export const MP3_BITRATE = 64_000;

const VIDEO_EXTENSIONS = new Set(["mp4", "m4v", "mov", "mkv"]);

export function isVideoFile(file: File): boolean {
  if (file.type.startsWith("video/")) return true;
  if (file.type !== "") return false;
  const dot = file.name.lastIndexOf(".");
  return dot !== -1 && VIDEO_EXTENSIONS.has(file.name.slice(dot + 1).toLowerCase());
}

/** Browsers have no MP3 encoder of their own; LAME is a lazy chunk of its own too. */
export async function ensureMp3Encoder(): Promise<void> {
  const { canEncodeAudio } = await import("mediabunny");
  if (!(await canEncodeAudio("mp3"))) {
    const { registerMp3Encoder } = await import("@mediabunny/mp3-encoder");
    registerMp3Encoder();
  }
}

/**
 * The video's primary audio track re-encoded to MP3. Throws when the file has
 * no audio or this browser cannot decode it; the caller decides whether to
 * fall back to uploading the video itself.
 */
export async function extractMp3(
  file: File,
  onProgress: (progress: number) => void,
  signal: AbortSignal,
): Promise<File> {
  const {
    ALL_FORMATS,
    BlobSource,
    BufferTarget,
    Conversion,
    Input,
    Mp3OutputFormat,
    Output,
    Quality,
  } = await import("mediabunny");
  await ensureMp3Encoder();

  const input = new Input({ source: new BlobSource(file), formats: ALL_FORMATS });
  try {
    if ((await input.getPrimaryAudioTrack()) === null) {
      throw new Error(`"${file.name}" has no audio track.`);
    }
    const target = new BufferTarget();
    const output = new Output({ format: new Mp3OutputFormat(), target });
    const conversion = await Conversion.init({
      input,
      output,
      tracks: "primary",
      video: { discard: true },
      audio: { codec: "mp3", numberOfChannels: 1, quality: new Quality(MP3_BITRATE) },
      showWarnings: false,
    });
    if (!conversion.isValid) {
      throw new Error("This browser cannot convert that video's audio.");
    }
    conversion.onProgress = (progress) => onProgress(progress);
    const abort = () => void conversion.cancel();
    signal.addEventListener("abort", abort, { once: true });
    try {
      await conversion.execute();
    } finally {
      signal.removeEventListener("abort", abort);
    }
    if (target.buffer === null) throw new Error("The audio conversion produced nothing.");

    const base = file.name.replace(/\.[^.]+$/, "");
    return new File([target.buffer], `${base}.mp3`, {
      type: "audio/mpeg",
      lastModified: file.lastModified,
    });
  } finally {
    input.dispose();
  }
}
